import React from 'react';
import { Card, Icon, EmptyState, Accordion, Table, TableBody, TableHeader, TableHeaderRow, TableHeaderCell, TooltipHelp, SelectHierarchyLabel, Status } from '@weezevent/nacre';

import { useTranslation } from 'react-i18next';
import RatesTreeModel from '../../../models/ratesTreeModel';

import ProductSaleModel from '../../../models/productSaleModel';
import LoadingCard, { LoadingOptions } from '../../../components/loadingCard';
import QuotasModel from '../../../models/quotasModel';
import { InfoTextTooltip } from '../../attendee/detail/components/infotext-tooltip/infoTextTooltip';

import PutForSaleAllRatesRow from './components/putForSaleAllRatesRow';
import PutForSaleRateGroupRow from './components/putForSaleRateGroupRow';
import PutForSaleRateRow from './components/putForSaleRateRow';

import Style from './putForSale.module.css';
import EventModel from '../../../models/eventModel';

const PREFIX = 'ticket.cross-selling.putForSale';

function EventAccordion({ onDelete, index, event, ratesArray, children }) {
    const { t } = useTranslation();

    const isDisplayed = React.useMemo(() => {
        return Boolean(ratesArray.filter(r => r.eventId === event.id && r.rates_list?.length).length);
    }, [ratesArray, event]);

    if (!isDisplayed) {
        return null;
    }

    return (
        <Accordion
            key={index}
            openByDefault={index === 0}
            title={
                <div className={Style['title-container']}>
                    <div className={Style['event-name']}>{event.name}</div>
                    <div
                        className={Style['delete']}
                        onClick={_e => {
                            _e.stopPropagation();
                            onDelete(event.id);
                        }}
                    >
                        {t('ticket.common.action.delete')}
                    </div>
                </div>
            }
        >
            {children}
        </Accordion>
    );
}

export default function PutForSale({
    organization,
    productId,
    category,
    setForSaleArray,
    forSaleArray,
    salesToDelete,
    onSalesToDelete,
    events,
    loadingEvents,
    querySearch,
    setQuerySearch
}) {
    const { t } = useTranslation();

    const [selectedEvents, setSelectedEvents] = React.useState([]);
    const [selectedEventsIds, setSelectedEventsIds] = React.useState([]);
    const [ratesArray, setRatesArray] = React.useState([]);
    const [quotas, setQuotas] = React.useState({});

    const query = React.useMemo(() => {
        if (!selectedEventsIds.length) {
            return { event__in: [] };
        }
        return { event__in: selectedEventsIds };
    }, [selectedEventsIds]);

    const [, loadForSale] = ProductSaleModel.useApiModel(
        {
            organizationId: organization.id,
            productId,
            cache: false,
            allow_cache: false,
            onSuccess: response => {
                setForSaleArray(
                    response.map(f => {
                        return { ...f.object, category };
                    })
                );
                setSelectedEventsIds(response.map(it => it.event));
            }
        },
        [organization, productId, category]
    );

    const [, loadingSelectedEvents] = EventModel.useApiModel(
        {
            organizationId: organization.id,
            cache: false,
            allow_cache: false,
            launch: Boolean(selectedEventsIds.length),
            query,
            onSuccess: response => {
                setSelectedEvents(response);
                response.forEach(event => {
                    let rateTree = RatesTreeModel.getAll({ organizationId: organization.id, eventId: event.id, query: { expand: 'relays', has_distributor: false } });
                    let _quotas = QuotasModel.getAll({ organizationId: organization.pk, eventId: event.id, getForRates: true, query: { id_date: 0 } });
                    Promise.all([rateTree, _quotas]).then(([rateTree, _quotas]) => {
                        setRatesArray(currentState => {
                            let r1 = [...currentState];
                            r1.push({
                                eventId: event.id,
                                selected: false,
                                rates_list: rateTree.rates_list,
                                root: rateTree.root,
                                rates_without_group: rateTree.rates_without_group
                            });
                            return r1;
                        });
                        setQuotas(currentState => {
                            let _currentState = { ...currentState };
                            _currentState[event.id] = _quotas.filter(q => q.event_id === event.id);
                            return _currentState;
                        });
                    });
                });
            }
        },
        [organization, query]
    );

    const onDelete = React.useCallback(
        eventId => {
            setSelectedEventsIds(currentState => {
                const newCurrentState = [...currentState].filter(e => e !== eventId);
                if (!newCurrentState.length) {
                    setSelectedEvents([]);
                }
                return newCurrentState;
            });
            setForSaleArray(currentState => {
                let newCurrentState = [...currentState];
                return newCurrentState.filter(f => f.event !== eventId);
            });
            onSalesToDelete(currentState => {
                return [...currentState, ...forSaleArray.filter(f => f.event === eventId && f.id)];
            });
        },
        [forSaleArray]
    );

    const dateFormat = item => {
        return `${new Date(item?.start_date).toLocaleDateString([], {
            weekday: 'short',
            day: '2-digit',
            month: 'long',
            year: 'numeric',
            timeZone: item?.timezone
        })}, ${new Date(item?.start_date).toLocaleTimeString([], {
            hour: '2-digit',
            minute: '2-digit',
            timeZone: item?.timezone
        })} `;
    };

    const getColorStatus = item => {
        let status;
        status = item?.publication_status;
        if (status === 'draft') {
            return 'orange';
        }
        if (status === 'closed') {
            return 'red';
        }
        return 'green';
    };

    const filteredEvents = React.useMemo(() => {
        if (!events || loadingEvents) {
            return LoadingOptions;
        }
        const selectedEventsIds = selectedEvents?.map(se => se.id);
        return events
            ?.filter(e => !selectedEventsIds?.includes(e.id))
            .map(i => ({
                text: (
                    <div className={Style['events-picker-options']}>
                        <InfoTextTooltip title={i.name} subtitle={dateFormat(i)} />
                        <div>
                            <Status responsive text={t(`ticket.rate_template.${i.publication_status}`)} color={getColorStatus(i)} />
                        </div>
                    </div>
                ),
                value: i.id,
                obj: i
            }));
    }, [events, loadingEvents, selectedEvents]);

    const onLoadLastEventRates = React.useCallback(
        eventId => {
            let rateTree = RatesTreeModel.getAll({ organizationId: organization.id, eventId: eventId, query: { expand: 'relays', has_distributor: false } });
            let _quotas = QuotasModel.getAll({ organizationId: organization.pk, eventId: eventId, getForRates: true, query: { id_date: 0 } });
            Promise.all([rateTree, _quotas]).then(([rateTree, _quotas]) => {
                setRatesArray(currentState => {
                    let r1 = [...currentState];
                    r1.push({ eventId: eventId, selected: false, rates_list: rateTree.rates_list, root: rateTree.root, rates_without_group: rateTree.rates_without_group });
                    return r1;
                });
                setQuotas(currentState => {
                    let _currentState = { ...currentState };
                    _currentState[eventId] = _quotas.filter(q => q.event_id === eventId);
                    return _currentState;
                });
                setForSaleArray(currentState => {
                    let newForSaleArray = [...currentState];
                    rateTree.rates_list.forEach(rate => {
                        newForSaleArray.push({
                            event: eventId,
                            is_mandatory_product: false,
                            is_opened_product: false,
                            category: category,
                            is_product_in_sale: false,
                            product: productId,
                            rate: rate.id
                        });
                    });
                    return newForSaleArray;
                });
            });
        },
        [forSaleArray, organization, category, productId]
    );

    const onChange = React.useCallback(
        eventId => {
            onLoadLastEventRates(eventId);
            setSelectedEventsIds(currentState => [...currentState, eventId]);
            setQuerySearch('');
        },
        [onLoadLastEventRates]
    );

    const isLoading = React.useMemo(() => {
        if (querySearch) {
            return false;
        }
        // in case there are no selected events => there is no api call to get rates
        // otherwise we wait until rateArray get fullfilled (api call finished)
        return Boolean(
            (selectedEvents?.length && !ratesArray.length) ||
                (loadingEvents && !events?.length) ||
                (loadForSale && !events?.length) ||
                (forSaleArray.length && loadingSelectedEvents)
        );
    }, [forSaleArray, ratesArray, loadingEvents, events, loadForSale, selectedEvents, loadingSelectedEvents, querySearch]);

    return (
        <Card>
            <Card.Header>
                <Card.Title title={t(`${PREFIX}.eventsAndRates`)} />
                <Card.SubTitle title={t(`${PREFIX}.eventsAndRatesSubtitles`)} />
            </Card.Header>
            <LoadingCard loading={isLoading}>
                <Card.Content>
                    <div className={Style['events-selector-container']}>
                        <SelectHierarchyLabel
                            searchable
                            className={Style['events-selector']}
                            label={t(`${PREFIX}.addEvent`)}
                            onChange={onChange}
                            externalOptions={filteredEvents}
                            onExternalSearch={value => setQuerySearch(value)}
                            searchValue={querySearch}
                            placeholder={t('ticket.common.action.select')}
                            placeholderSearch={t('ticket.common.action.search')}
                        />
                    </div>
                    {!loadingEvents && !events?.length ? (
                        <EmptyState icon={<Icon name="list" />} title={t(`${PREFIX}.addEvent`)} subtitle={t(`${PREFIX}.eventsAndRatesTooltip`)} />
                    ) : (
                        selectedEvents?.map((e, i) => {
                            const correspondingEvent = ratesArray?.find(event => event.eventId === e.id);
                            return (
                                <EventAccordion key={i} event={e} onDelete={onDelete} index={i} ratesArray={ratesArray} PREFIX={PREFIX}>
                                    <Table>
                                        <TableHeader>
                                            <TableHeaderRow>
                                                <TableHeaderCell>{t('ticket.common.rates')}</TableHeaderCell>
                                                <TableHeaderCell>
                                                    {t(`${PREFIX}.mandatory`)}
                                                    <TooltipHelp title={t(`${PREFIX}.mandatoryTooltip`)} mini={false} inline />
                                                </TableHeaderCell>
                                                <TableHeaderCell>
                                                    {t(`${PREFIX}.unfolded`)}
                                                    <TooltipHelp title={t(`${PREFIX}.unfoldedTooltip`)} mini={false} inline />
                                                </TableHeaderCell>
                                                <TableHeaderCell content={t(`${PREFIX}.visibility`)} />
                                                <TableHeaderCell content={t(`${PREFIX}.sellStatus`)} />
                                            </TableHeaderRow>
                                        </TableHeader>
                                        <TableBody>
                                            <PutForSaleAllRatesRow
                                                key={productId}
                                                productId={productId}
                                                category={category}
                                                forSaleArray={forSaleArray}
                                                setForSaleArray={setForSaleArray}
                                                ratesArray={ratesArray}
                                                event={e}
                                            />
                                            {correspondingEvent?.rates_without_group.map(r => {
                                                return (
                                                    <PutForSaleRateRow
                                                        key={r.id}
                                                        productId={productId}
                                                        rate={r}
                                                        event={e}
                                                        category={category}
                                                        forSaleArray={forSaleArray}
                                                        setForSaleArray={setForSaleArray}
                                                        quotas={quotas[e.id] || []}
                                                    />
                                                );
                                            })}

                                            {correspondingEvent?.root.map(gr => (
                                                <PutForSaleRateGroupRow
                                                    key={gr.id}
                                                    rateGroup={gr}
                                                    isSubGroup={false}
                                                    productId={productId}
                                                    event={e}
                                                    category={category}
                                                    forSaleArray={forSaleArray}
                                                    setForSaleArray={setForSaleArray}
                                                    quotas={quotas[e.id] || []}
                                                />
                                            ))}
                                        </TableBody>
                                    </Table>
                                </EventAccordion>
                            );
                        })
                    )}
                </Card.Content>
            </LoadingCard>
        </Card>
    );
}
